import { useEffect, useRef } from 'react';
import axios from 'axios';

// Stale-while-revalidate cache for read-only GET endpoints used by the main tabs.
const FRESH_MS = 30 * 1000;
export const API_CACHE_EVENT = 'api-cache-updated';

const RULES = [
  { re: /^\/(projects|users|clients|user-status-permissions)(\?|$)/, swr: true },
  { re: /^\/projects\/(lead-creators|stale)(\?|$)/, swr: true },
  { re: /^\/reports\/(?!.*export)/, swr: true },
  // Polled counters: reuse for 30 s when switching tabs, never show stale on poll
  { re: /^\/(lead-inbox\/count|notifications\/unread-count)(\?|$)/, swr: false },
];

const cache = new Map();
const inflight = new Map();
let generation = 0;

export const clearApiCache = () => {
  generation += 1;
  cache.clear();
  inflight.clear();
};

const getAuth = (config) => {
  const h = config.headers || {};
  return (typeof h.get === 'function' ? h.get('Authorization') : h.Authorization) || '';
};

const ruleFor = (config) => {
  if ((config.method || 'get').toLowerCase() !== 'get') return null;
  if (config.responseType && config.responseType !== 'json') return null;
  const uri = axios.getUri(config);
  const idx = uri.indexOf('/api/');
  if (idx === -1) return null;
  const path = uri.slice(idx + 4);
  const rule = RULES.find((r) => r.re.test(path));
  return rule ? { rule, key: `${uri}|${getAuth(config)}` } : null;
};

const fetchAndStore = (key, config, base) => {
  let p = inflight.get(key);
  if (!p) {
    const gen = generation;
    // Snapshot keeps the raw (unparsed) body, so every caller gets freshly parsed data
    p = base(config).then((res) => {
      const snap = { ...res };
      if (gen === generation && res.status >= 200 && res.status < 300) {
        cache.set(key, { response: snap, time: Date.now() });
      }
      return snap;
    }).finally(() => { if (inflight.get(key) === p) inflight.delete(key); });
    inflight.set(key, p);
  }
  return p.then((r) => ({ ...r, config }));
};

const revalidate = (key, config, base, hit) => {
  if (inflight.has(key)) return;
  fetchAndStore(key, config, base)
    .then((res) => {
      if (res.data !== hit.response.data) window.dispatchEvent(new CustomEvent(API_CACHE_EVENT));
    })
    .catch(() => {});
};

export const installApiCache = () => {
  if (axios.defaults.__apiCacheInstalled) return;
  const base = axios.getAdapter(axios.defaults.adapter);
  axios.defaults.adapter = async (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && !/\/api\/ai\//.test(axios.getUri(config))) {
      // Any write invalidates cached lists so other tabs never show pre-save data
      clearApiCache();
      try {
        return await base(config);
      } finally {
        clearApiCache();
      }
    }
    const match = ruleFor(config);
    if (!match) return base(config);
    const { rule, key } = match;
    const hit = cache.get(key);
    if (hit) {
      if (Date.now() - hit.time < FRESH_MS) return { ...hit.response, config };
      if (rule.swr) {
        revalidate(key, config, base, hit);
        return { ...hit.response, config };
      }
    }
    return fetchAndStore(key, config, base);
  };
  axios.defaults.__apiCacheInstalled = true;
};

// Re-run a fetch function when background revalidation brought newer data
export const useApiCacheRefresh = (callback) => {
  const ref = useRef(callback);
  ref.current = callback;
  useEffect(() => {
    const handler = () => ref.current?.();
    window.addEventListener(API_CACHE_EVENT, handler);
    return () => window.removeEventListener(API_CACHE_EVENT, handler);
  }, []);
};
